import {
  Button,
  TextField,
  MenuItem,
  Box,
  DialogActions,
  DialogContent,
  Stack,
  DialogTitle,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import * as yup from "yup";
import { dateToServer, prettify, renderDateUTC } from "../../../utils/utils";
import { useNewDoctor } from "../useDoctorQueries";
import { profiles } from "..";
import { useGetOrganizations } from "../../HandlingComponents";
import { useAuthUser } from "react-auth-kit";

const validationSchema = yup.object({
  first_name: yup.string("Nome (*)").required("Nome è un campo obbligatorio!"),
  last_name: yup
    .string("Cognome (*)")
    .required("Cognome è un campo obbligatorio!"),
  email: yup
    .string("Email (*)")
    .email("Email (*)")
    .required("Email è un campo obbligatorio!"),
  mobile: yup.string("Cellulare (*)"),
  iban: yup.string("IBAN"),
  piva: yup.string("P. IVA"),
  profile: yup
    .string("Profilo (*)")
    .required("Profilo è un campo obbligatorio!"),

  // date_of_birth: yup
  // .string('Data di nascita (*)')
  // .required('Data di nascita è un campo obbligatorio!')
});

function AddDoctor({ closeModal }) {
  // === web service querying - POST new Doctor ===
  const newDoctorMutation = useNewDoctor(closeModal);
  const organizationQuery = useGetOrganizations();
  const authUser = useAuthUser();

  // === Form handling ===
  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      profile: "",
      email: "",
      mobile: "",
      date_of_birth: null,
      piva: "",
      iban: "",
      note: "",
      org_id: 1,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      let newDoctor = {
        ...values,
        date_of_birth: dateToServer(values.date_of_birth),
        org_id: authUser().role === 1 ? values.org_id : authUser().user.org_id,
      };

      newDoctorMutation.mutate(newDoctor);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle>Nuovo professionista sanitario</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={8} sx={{ width: "100%" }}>
          <Box sx={{ float: "left", width: "50%" }}>
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              id="first_name"
              name="first_name"
              label="Nome (*)"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.first_name}
              error={
                formik.touched.first_name && Boolean(formik.errors.first_name)
              }
              helperText={formik.touched.first_name && formik.errors.first_name}
            />

            <TextField
              margin="dense"
              fullWidth
              id="last_name"
              name="last_name"
              label="Cognome (*)"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.last_name}
              error={
                formik.touched.last_name && Boolean(formik.errors.last_name)
              }
              helperText={formik.touched.last_name && formik.errors.last_name}
            />

            <TextField
              margin="dense"
              fullWidth
              id="profile"
              name="profile"
              label="Profilo (*)"
              select
              variant="standard"
              defaultValue={""}
              onChange={formik.handleChange}
              value={formik.values.profile}
            >
              {profiles.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {prettify(option.label)}
                </MenuItem>
              ))}
            </TextField>

            <DatePicker
              id="date_of_birth"
              format="DD/MM/YYYY"
              name="date_of_birth"
              disableFuture
              label="Data di nascita (*)"
              sx={{ marginTop: "2ch" }}
              value={formik.values.date_of_birth}
              onChange={(value) =>
                formik.setFieldValue("date_of_birth", value, true)
              }
              slotProps={{
                textField: {
                  variant: "standard",
                  error:
                    formik.touched.date_of_birth &&
                    Boolean(formik.errors.date_of_birth),
                  helperText:
                    formik.touched.date_of_birth && formik.errors.date_of_birth,
                },
              }}
            />

            <TextField
              margin="dense"
              fullWidth
              id="email"
              name="email"
              label="Email (*)"
              type="email"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.email}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Box>

          <Box sx={{ float: "right", width: "50%" }}>
            <TextField
              margin="dense"
              fullWidth
              id="mobile"
              name="mobile"
              label="Cellulare"
              type="tel"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.mobile}
              error={formik.touched.mobile && Boolean(formik.errors.mobile)}
              helperText={formik.touched.mobile && formik.errors.mobile}
            />

            {organizationQuery.isSuccess && authUser().role === 1 && (
              <TextField
                margin="dense"
                fullWidth
                select
                id="org_id"
                name="org_id"
                label="Organizzazione"
                variant="standard"
                onChange={formik.handleChange}
                value={formik.values.org_id}
                error={formik.touched.org_id && Boolean(formik.errors.org_id)}
                helperText={formik.touched.org_id && formik.errors.org_id}
              >
                {organizationQuery.data.organizations.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              margin="dense"
              fullWidth
              id="piva"
              name="piva"
              label="Partita IVA"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.piva}
            />

            <TextField
              margin="dense"
              fullWidth
              id="iban"
              name="iban"
              label="IBAN"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.iban}
            />

            <TextField
              margin="dense"
              fullWidth
              id="note"
              name="note"
              label="Note"
              multiline
              minRows={4}
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.note}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={newDoctorMutation.isLoading} type="submit">
          Conferma
        </Button>
        <Button
          disabled={newDoctorMutation.isLoading}
          onClick={formik.handleReset}
        >
          Reset
        </Button>
        <Button
          disabled={newDoctorMutation.isLoading}
          color="error"
          onClick={closeModal}
        >
          Annulla
        </Button>
      </DialogActions>
    </form>
  );
}

export default AddDoctor;
